<template>
    <section>
        <!-- Themaoverzicht: zolang er nog geen thema gekozen is, tonen we de thema's
             waarop de bezoeker kan doorklikken in plaats van alle dienstverleners. -->
        <template v-if="showThemes">
            <p class="d-none d-md-block text--secondary font-italic pl-3">Kies een thema om het aanbod te bekijken</p>
            <ul class="list-unstyled theme-overview">
                <li v-for="choice in themeFacet.choices" :key="choice.id" class="mb-3">
                    <v-card
                        class="theme-card"
                        elevation="0"
                        @click="selectTheme(choice)"
                    >
                        <v-card-text class="d-flex align-center py-4">
                            <div class="flex-grow-1 pr-3">
                                <strong class="theme-card__title d-block">{{ choice.value }}</strong>
                                <span v-if="choice.description" class="text--secondary">{{ choice.description }}</span>
                            </div>
                            <span class="text--secondary mr-3">{{ themeCount(choice) }}</span>
                            <v-icon>fal fa-angle-right</v-icon>
                        </v-card-text>
                    </v-card>
                </li>
            </ul>
        </template>

        <!-- Thema zonder aanbod: bezoekers kunnen zich aanmelden voor de wachtlijst. -->
        <WaitlistForm v-else-if="themeWithoutOffer" :theme="selectedTheme"></WaitlistForm>

        <!-- Dienstverleners binnen het gekozen thema (of wanneer er geen thema-facet is). -->
        <template v-else>
            <p class="d-none d-md-block text--secondary font-italic pl-3">
                <span v-if="filteredOrganisations.length > 1">{{ filteredOrganisations.length }} dienstverleners gevonden</span>
                <span v-else-if="filteredOrganisations.length <= 0">Geen dienstverleners gevonden</span>
                <span v-else>Eén dienstverlener gevonden</span>
            </p>
            <ul class="list-unstyled">
                <li v-for="organisation in paginatedOrganisations" :key="organisation.uuid" class="mb-4">
                    <OrganisationCard :organisation="organisation"></OrganisationCard>
                </li>
            </ul>
            <v-pagination
                v-if="pageLength > 1"
                v-model="page"
                :length="pageLength"
                circle
                prev-icon="fal fa-angle-left"
                next-icon="fal fa-angle-right"
                :total-visible="($vuetify.breakpoint.mdAndDown) ? 5 : 10"
                class="mt-7"
            ></v-pagination>
        </template>
    </section>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import OrganisationCard from "./OrganisationCard";
import WaitlistForm from "./WaitlistForm";

export default {
    name: "OrganisationList",
    components: {OrganisationCard, WaitlistForm},
    props: {
        organisationsPerPage: {
            type: Number,
            default: 5,
        },
    },
    computed: {
        // Het thema-facet (voorheen "Categorie"). We accepteren beide namen zodat
        // de weergave blijft werken ongeacht of de databasemigratie al gedraaid is.
        themeFacet () {
            return this.visibleFacets.find(facet => facet.name === 'Thema' || facet.name === 'Categorie') || null;
        },
        themeSelected () {
            if (!this.themeFacet) {
                return false;
            }
            const selected = this.filters.facetChoices[this.themeFacet.id];
            return Array.isArray(selected) && selected.length > 0;
        },
        // Toon de thema-tegels zolang er nog geen thema gekozen is, ook wanneer de
        // pagina niet via een dashboardknop is geopend (de facetten worden altijd
        // tot één thema-facet samengevoegd).
        showThemes () {
            return !!this.themeFacet && !this.themeSelected;
        },
        selectedTheme () {
            if (!this.themeSelected) {
                return null;
            }
            const selectedId = this.filters.facetChoices[this.themeFacet.id][0];
            return this.themeFacet.choices.find(choice => choice.id === selectedId) || null;
        },
        // Er is helemaal geen dienstverlener binnen het gekozen thema, los van de overige
        // filters (die kunnen de lijst ook leeg maken terwijl er wel aanbod is).
        themeWithoutOffer () {
            if (!this.selectedTheme) {
                return false;
            }
            const choiceIds = this.selectedTheme.ids || [this.selectedTheme.id];
            return !this.organisations.some(organisation =>
                organisation.facetChoices.some(c => choiceIds.includes(c.id))
            );
        },
        paginatedOrganisations () {
            const offset = (this.page - 1) * this.organisationsPerPage;
            return this.filteredOrganisations.slice(offset, offset + this.organisationsPerPage);
        },
        page: {
            get () { return this.organisationPage; },
            set (value) {
                this.$store.dispatch('organisation/updateOrganisationPage', value);
                // De paginering staat onderaan; spring terug naar boven zodat de nieuwe pagina zichtbaar is.
                this.$vuetify.goTo(0);
            }
        },
        pageLength () {
            return Math.ceil(this.filteredOrganisations.length / this.organisationsPerPage);
        },
        ...mapGetters('organisation', ['visibleFacets']),
        ...mapState({
            organisations: state => state.organisation.organisations,
            filteredOrganisations: state => state.organisation.filteredOrganisations,
            organisationPage: state => state.organisation.organisationPage,
            filters: state => state.organisation.filters,
        }),
    },
    methods: {
        selectTheme (choice) {
            this.$store.dispatch('organisation/updateFacetFilter', {
                facet: this.themeFacet,
                choices: [choice.id],
            });
        },
        themeCount (choice) {
            const choiceIds = choice.ids || [choice.id];
            return this.filteredOrganisations.filter(organisation =>
                organisation.facetChoices.some(c => choiceIds.includes(c.id))
            ).length;
        },
        resetPagination () {
            this.page = 1;
        },
    },
}
</script>

<style scoped>
.theme-card {
    cursor: pointer;
    border: 1px solid #e0e0e0;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.theme-card:hover {
    border-color: var(--v-primary-base);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.theme-card__title {
    font-size: 1.05rem;
}
</style>
